/** @jsxImportSource @emotion/react */
import React, { useContext, useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import useEvent from 'react-use-event-hook';
import _ from 'lodash';
import { addClass, hasClass, DataType } from '@grapecity/wijmo';
import { CellType, DataMap } from '@grapecity/wijmo.grid';
import { Button, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { useMessageBar } from 'hooks/useMessageBar';
import { useCommonModal } from 'hooks/useCommonModal';
import { useLoading } from 'components/process/Loading';
import { getCommonCodeNames, getCommonCodeNamesCondition } from 'apis/admin/CommonCode';
import {
  ControlBtnGroup,
  GlobalBtnGroup,
  SubTitleGroup,
  SubTitleLayout,
} from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import { MdResultCondition, MdResultDetail, MdResultMaster } from 'models/md/MdResult';
import { CommonUtil } from 'utils/CommonUtil';
import { hasRole } from 'utils/SessionUtil';
import { saveMdResults, findMdResultsMasterById, getMdResultsDetailList } from 'apis/md/MdResult';
import { ComboBox } from 'components/selects/ComboBox';
import CustomGrid from 'components/grids/CustomGrid';
import CustomDatepicker from 'components/inputs/CustomDatepicker';
import { tb } from 'components/layouts/Table';
import { CrudCode } from 'models/common/Edit';
import { PageMode } from 'models/common/Common';
import { MenuContextType } from 'models/admin/Menu';
import { MenuContext } from 'App';
import { findMdIvesAtcRegList } from 'apis/md/MdIvesAtcRegt';
import { findMdModel } from 'apis/md/MdModel';
import { GridStatusCellTemplate } from 'components/grids/GridStatusCellRenderer';

interface PageLocationState {
  mode?: PageMode;
  mdRltId?: string;
}

/**
 * @desc 투자비관리 > 형개발 투자비 산출 > 투자이력 Database
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.05.13   jshwang12       신규 생성
 */
const MdIvesHisInqRegPage = () => {
  const gridRef = useRef<any>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { openLoading } = useLoading();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();
  const [code, setCode] = useState<any>();
  const [condition, setCondition] = useState<MdResultCondition>();
  const [rowData, setRowData] = useState<MdResultDetail[]>([]);
  const [masterData, setMasterData] = useState<MdResultMaster>({});
  const [errors, setErrors] = useState<any>({});
  const menuContext = useContext<MenuContextType>(MenuContext);
  const [searchParams] = useSearchParams();
  const { state } = useLocation();
  const [locationState, setLocationState] = useState<PageLocationState>(useLocation().state);
  const [hasAuth, setHasAuth] = useState<boolean>(false);

  const [pageMode, setPageMode] = useState<string>(locationState?.mode ?? 'WRITE');
  const [mdRltId, setMdRltId] = useState<string>(locationState.mdRltId ?? '');

  useEffect(() => {
    if (hasRole('MD_IVES_MANAGER') || hasRole('ADM')) {
      setHasAuth(true);
    }
    getCommonCodes();
    handleSearch(mdRltId);
  }, []);

  useEffect(() => {
    if (!locationState?.mdRltId && PageMode.WRITE === locationState?.mode) {
      // 자동 키 생성 필요 시 여기서
    } else if (searchParams?.get('mdRltId')) {
      const id = searchParams?.get('mdRltId') || '';
      // 형개발결과ID 있는 경우 수정화면으로 변경
      setLocationState((prev) => ({
        ...prev,
        mode: PageMode.EDIT,
        mdRltId: id,
      }));
      setMdRltId(id);
      handleSearch(id);
    } else {
      if (!state) {
        openMessageBar({
          type: 'error',
          content: t('com.label.잘못된 접근입니다.', '잘못된 접근입니다.'),
        });
        menuContext.closeCurrentTab(menuContext);
        return;
      }
    }
  }, []);

  useEffect(() => {
    if (state) {
      if (locationState) {
        // setMdRltId(locationState.mdRltId || searchParams.get('mdRltId') || '');
        // 목록에서 다른 항목을 선택한 경우 (신규, 수정 탭이 모두 열려있는 경우 구분하기 위해 조건 추가)
        setLocationState((prev) => (prev?.mode === state?.mode ? state : prev));
      } else {
        setLocationState(state);
      }
    }
  }, [state]);

  useEffect(() => {
    setPageMode(locationState?.mode || PageMode.WRITE);
  }, [locationState]);

  const isEditable = (column = '') => {
    // 신규모드인 경우
    if (pageMode === PageMode.WRITE || pageMode === PageMode.EDIT) {
      return true;
    }
    return false;
  };

  const mapDisplayStacking = useMemo(() => {
    return Object.fromEntries((code?.mdStackingTp || []).map((o) => [o.cmnCd, o.optValCtn1]));
  }, [code?.mdStackingTp]);

  const getCommonCodes = () => {
    Promise.all([
      getCommonCodeNames('BILD_PLNT_CD'), // 플랜트
      getCommonCodeNames('MD_CPTN_TP_CD'), // 조성
      getCommonCodeNames('MD_STACKING_TP'), // Stacking Type
      getCommonCodeNames('MD_CELL_TP'), // 셀 Type
      getCommonCodeNames('MD_ROL_PRESS_TP'), // 압엽사양
      getCommonCodeNamesCondition({
        cmnGrCd: 'PRDN_PROC_CD',
        optValCtn7: 'Y',
      }), // 공정
      getCommonCodeNamesCondition({
        cmnGrCd: 'MD_EQCL_ID',
        optValCtn2: 'Y',
      }), // 설비군
      getCommonCodeNames('STND_YN'), // 구분 (표준/비표준)
      findMdIvesAtcRegList({}), // 투자항목 조회
      findMdModel(), // 모델 조회
    ]).then(
      ([
        bildPlntCd,
        mdCptnTpCd,
        mdStackingTp,
        mdCellTp,
        mdRolPressTp,
        prdnProcCd,
        mdEqclId,
        stndYn,
        mdAtcList,
        mdMdlList,
      ]) => {
        setCode({
          bildPlntCd: bildPlntCd,
          mdCptnTpCd: mdCptnTpCd,
          mdStackingTp: mdStackingTp,
          mdCellTp: mdCellTp,
          mdRolPressTp: mdRolPressTp,
          prdnProcCd: prdnProcCd,
          mdEqclId: mdEqclId,
          stndYn: stndYn,
          mdAtcList: mdAtcList,
          mdMdlList: mdMdlList,
        });
      }
    );
  };

  const handleMasterChange = (name: string, value: any) => {
    setMasterData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: false }));
  };

  const handleReset = () => {
    if (mdRltId) {
      handleSearch(mdRltId);
    }
  };

  /**
   * 조회
   */
  const handleSearch = (pMdRltId) => {
    if (pMdRltId) {
      Promise.all([findMdResultsMasterById(pMdRltId), getMdResultsDetailList(pMdRltId)]).then(
        ([master, detail]) => {
          setMasterData(master);
          setRowData(detail);
        }
      );
    } else {
      setMasterData({});
      setRowData([]);
    }
  };

  const handleDownloadExcel = () => {
    CommonUtil.exportWijmoExcelGrid(
      gridRef.current,
      t('md.label.투자이력 Database 목록', '투자이력 Database 목록'),
      t('md.label.투자이력 Database 목록', '투자이력 Database 목록')
    );
  };

  const layoutDefinition = useMemo(() => {
    const mapMdEqclId = new DataMap(code?.mdEqclId || [], 'cmnCd', 'cmnCdNm');
    mapMdEqclId.getDisplayValues = (item) => {
      return (code?.mdEqclId || [])
        .reduce((acc, cur) => {
          if (acc.findIndex((o) => o.cmnCd === cur.cmnCd) < 0) {
            acc.push(cur);
          }
          return acc;
        }, [])
        .map((o) => o.cmnCdNm || '');
    };

    const mapIvesAtcList = new DataMap(code?.mdAtcList || [], 'ivesAtcId', 'ivesAtcNm');
    mapIvesAtcList.getDisplayValues = (item) => {
      return (code?.mdAtcList || [])
        .filter((o) => o.stndYn === item.stndYn)
        .reduce((acc, cur) => {
          if (acc.findIndex((o) => o.ivesAtcId === cur.ivesAtcId) < 0) {
            acc.push(cur);
          }
          return acc;
        }, [])
        .map((o) => o.ivesAtcNm || '');
    };

    return [
      {
        binding: 'no',
        header: String(t('com.label.NO', 'NO')),
        width: 40,
        minWidth: 40,
        align: 'center',
        isReadOnly: true,
        cellTemplate: (grid) => grid.row._idx + 1,
      },
      {
        header: String(t('com.label.상태', '상태')),
        binding: 'crudKey',
        minWidth: 43,
        width: 43,
        isReadOnly: true,
        align: 'center',
        cellTemplate: GridStatusCellTemplate,
      },
      {
        binding: 'mdlId',
        header: String(t('md.label.모델', '모델')),
        width: 70,
        minWidth: 50,
        align: 'center',
        isRequired: false,
        cssClass: 'WijmoSelect',
        dataMap: new DataMap(code?.mdMdlList || [], 'mdlId', 'mdlNm'),
      },
      {
        binding: 'prdnLnNm',
        header: String(t('md.label.호기', '호기')),
        width: 100,
        minWidth: 80,
        align: 'center',
        isRequired: false,
      },
      {
        binding: 'fertHircStrcCd',
        header: String(t('md.label.조성', '조성')),
        width: 100,
        minWidth: 80,
        align: 'center',
        isReadOnly: true, // 제품계층구조코드 (모델사양: S00020)
        cssClass: 'WijmoSelect',
        dataMap: new DataMap(code?.mdCptnTpCd || [], 'cmnCd', 'cmnCdNm'),
      },
      {
        binding: 'stakTpCd',
        header: String(t('md.label.Stacking Type', 'Stacking Type')),
        width: 124,
        minWidth: 50,
        align: 'center',
        isReadOnly: true, // 스택유형코드 (모델사양: S00001)
        cssClass: 'WijmoSelect',
        dataMap: new DataMap(code?.mdStackingTp || [], 'cmnCd', 'cmnCdNm'),
      },
      {
        binding: 'cellTpCd',
        header: String(t('md.label.셀 Type', '셀 Type')),
        width: 90,
        minWidth: 50,
        align: 'center',
        isReadOnly: true, // 셀유형코드 (모델사양: S00005)
        cssClass: 'WijmoSelect',
        dataMap: new DataMap(code?.mdCellTp || [], 'cmnCd', 'cmnCdNm'),
      },
      {
        binding: 'rprsSpecCd',
        header: String(t('md.label.압연사양', '압연사양')),
        width: 90,
        minWidth: 50,
        align: 'center',
        isReadOnly: true, // 압연사양코드 (모델사양: S00115)
        cssClass: 'WijmoSelect',
        dataMap: new DataMap(code?.mdRolPressTp || [], 'cmnCd', 'cmnCdNm'),
      },
      {
        binding: 'stakFoldQty',
        header: String(t('md.label.Stack', 'Stack')),
        width: 40,
        minWidth: 40,
        align: 'center',
        isReadOnly: true, // 스택폴딩수량 (모델사양: S00045)
      },
      {
        binding: 'stackDisplay',
        header: String(t('md.label.Stack', 'Stack')),
        width: 40,
        minWidth: 40,
        align: 'center',
        isReadOnly: true,
        cellTemplate: (params) => {
          return mapDisplayStacking?.[params.item?.stakTpCd || ''] || '';
        },
      },
      {
        binding: 'prdnProcCd',
        header: String(t('md.label.공정', '공정')),
        width: 100,
        minWidth: 50,
        align: 'center',
        isRequired: false,
        cssClass: 'WijmoSelect',
        dataMap: new DataMap(code?.prdnProcCd || [], 'cmnCd', 'cmnCdNm'),
      },
      {
        binding: 'eqclId',
        header: String(t('md.label.설비', '설비')),
        width: 120,
        minWidth: 80,
        align: 'center',
        isRequired: false,
        cssClass: 'WijmoSelect',
        dataMap: mapMdEqclId,
      },
      {
        binding: 'stndYn',
        header: String(t('md.label.구분', '구분')),
        width: 80,
        minWidth: 50,
        align: 'center',
        isRequired: false,
        cssClass: 'WijmoSelect',
        dataMap: new DataMap(code?.stndYn || [], 'cmnCd', 'cmnCdNm'),
      },
      {
        binding: 'ivesAtcId',
        header: String(t('md.label.투자항목', '투자항목')),
        width: 100,
        minWidth: 80,
        align: 'left',
        isRequired: false,
        cssClass: 'WijmoSelect',
        dataMap: mapIvesAtcList,
      },
      {
        binding: 'ivesDtlDesc',
        header: String(t('md.label.투자상세', '투자상세')),
        width: '*',
        minWidth: 250,
        align: 'left',
        isRequired: false,
      },
      {
        binding: 'quotUprc',
        header: String(t('md.label.단가', '단가')),
        width: 80,
        minWidth: 50,
        align: 'right',
        isRequired: false,
        dataType: DataType.String,
      },
      {
        binding: 'uprcDcnCtn',
        header: String(t('md.label.단가기준', '단가기준')),
        width: 100,
        minWidth: 80,
        align: 'left',
        isRequired: false,
      },
      {
        binding: 'chkCtn',
        header: String(t('md.label.확인 필요', '확인 필요')),
        width: 100,
        minWidth: 80,
        align: 'left',
        isRequired: false,
      },
      {
        binding: 'fertMdlDesc',
        header: String(t('md.label.기존 양산 모델', '기존 양산 모델')),
        width: 130,
        minWidth: 80,
        align: 'left',
        isRequired: false,
      },
    ];
  }, [mapDisplayStacking]);

  const onItemFormatter = useEvent((panel, row, col, cell) => {
    if (CellType.Cell === panel.cellType) {
      const binding = panel.columns[col].binding;
      const item = panel.rows[row].dataItem;
      if (!hasAuth || !(PageMode.EDIT === pageMode || PageMode.WRITE === pageMode)) {
        cell.ariaReadOnly = true;
        if (cell.firstElementChild instanceof HTMLButtonElement) {
          cell.firstChild.remove();
        }
      }
    }
  });

  const onBeginningEdit = useEvent((grid, e) => {
    const col = grid.columns[e.col];
    const binding = col.binding;
    const item = grid.rows[e.row].dataItem;

    if (!hasAuth || !(PageMode.EDIT === pageMode || PageMode.WRITE === pageMode)) {
      e.cancel = true;
    }
  });

  const onCellEditEnding = useEvent((grid, e) => {
    const binding = grid.columns[e.col].binding;
    const item = grid.rows[e.row].dataItem;
    const oldVal = grid.getCellData(e.row, e.col);
    const newVal = grid.activeEditor?.value;
    // 모델 변경
    if ('mdlId' === binding) {
      const newModel = (code?.mdMdlList || []).find((o) => o.mdlNm === newVal);
      if (oldVal !== newModel?.mdlNm) {
        // 선택한 모델에 해당하는 사양값 설정
        item.fertHircStrcCd = newModel?.fertHircStrcCd || ''; // 제품계층구조코드 (모델사양: S00020)
        item.stakTpCd = newModel?.stakTpCd || ''; // 스택유형코드 (모델사양: S00001)
        item.cellTpCd = newModel?.cellTpCd || ''; // 셀유형코드 (모델사양: S00005)
        item.rprsSpecCd = newModel?.rprsSpecCd || ''; // 압연사양코드 (모델사양: S00115)
        item.stakFoldQty = newModel?.stakFoldQty || ''; // 스택폴딩수량 (모델사양: S00045)
      }
    }
    // 구분 (표준/비표준)
    else if ('stndYn' === binding) {
      const newStndYn = (code?.stndYn || []).find((o) => o.cmnCdNm === newVal)?.cmnCd || '';
      // 값이 변한 경우 초기화
      if (oldVal !== newStndYn) {
        item.ivesAtcId = '';
      }
    }
    // 단가
    else if ('quotUprc' === binding) {
      // 숫자만 입력 가능
      if (newVal) {
        if (!/^[0-9.]+$/.test(newVal)) {
          e.cancel = true;
          return;
        }
        const value = parseFloat(newVal);
        if (!_.isNaN(value)) {
          setTimeout(() => {
            grid.setCellData(e.row, e.col, CommonUtil.roundNumber(value, 2));
          }, 0);
        }
      }
    }
  });

  const onCellEditEnded = useEvent((grid, e) => {
    const col = grid.columns[e.col];
    const binding = col.binding;
    const item = grid.rows[e.row].dataItem;
  });

  const onInitialized = (grid) => {
    gridRef.current = grid;
    grid.itemFormatter = onItemFormatter;

    grid.formatItem.addHandler((s, e) => {
      const item = e.panel.rows[e.row]?.dataItem;
      if (e.panel.cellType === CellType.TopLeft && e.cell.querySelector('input').indeterminate) {
        e.cell.querySelector('input').checked = true;
      }
      if (e.panel === s.rowHeaders) {
        if (e.cell.hasChildNodes()) {
          // 이미 사용중인 항목인 경우 선택불가/readonly
          if (item?.usedYn === 'Y') {
            addClass(e.cell, 'wj-state-disabled');
          }
        }
      }
    });

    grid.hostElement.addEventListener('click', (e) => {
      // 전체 선택 시
      if (hasClass(e.target, 'wj-column-selector-group') && e.target.checked) {
        grid.deferUpdate(() => {
          grid.rows.forEach((row) => {
            if (row?.dataItem?.usedYn === 'Y') {
              row.isSelected = false;
            }
          });
        });
      }
    });
  };

  /**
   * 행추가
   */
  const handleAddRow = () => {
    const newRow = {
      crudKey: CrudCode.CREATE,
    } as MdResultDetail;
    setRowData([...rowData, newRow]);
  };

  /**
   * 행복사
   */
  const handleCopyRow = useCallback(() => {
    const selectedRows = gridRef.current?.rows?.filter((r) => r.isSelected);
    if ((selectedRows || []).length < 1) {
      openMessageBar({
        type: 'error',
        content: t('com.label.복사할 행을 선택해 주세요.', '복사할 행을 선택해 주세요.'),
      });
      return;
    }
    const newRows = [] as MdResultDetail[];
    selectedRows.forEach((o) => {
      const item = o.dataItem;
      newRows.push({
        ...item,
        crudKey: CrudCode.CREATE,
        mdRltDtlId: '',
        dataInsUserNm: '',
        dataInsDtm: '',
        dataUpdUserNm: '',
        dataUpdDtm: '',
      });
    });
    setRowData([...newRows, ...rowData]);
  }, [rowData]);

  /**
   * 행삭제
   */
  const handleDeleteRow = () => {
    const selectedRows = gridRef.current.rows.filter((r) => r.isSelected);

    if ((selectedRows || []).length < 1) {
      openMessageBar({
        type: 'error',
        content: t('com.label.삭제할 데이터를 선택해 주세요.', '삭제할 데이터를 선택해 주세요.'),
      });
      return;
    }

    const selectedIds = selectedRows
      .map((row) => {
        return parseInt(row.index!);
      })
      .reverse();

    selectedIds.forEach((item) => {
      if (rowData[item].crudKey === CrudCode.CREATE) {
        // 각 선택된 아이템에 대해 삭제 로직 수행
        delete rowData[item];
      } else {
        rowData[item].crudKey = CrudCode.DELETE;
      }
    });

    const filteredData = rowData.filter((element) => element !== undefined);
    setRowData(filteredData);
  };

  const validate = () => {
    const error = {};
    if (!masterData?.bildPlntCd) {
      Object.assign(error, { bildPlntCd: true });
    }
    if (!masterData?.cnsnAprvDt) {
      Object.assign(error, { cnsnAprvDt: true });
    }
    if (!masterData?.wbsCdNm) {
      Object.assign(error, { wbsCdNm: true });
    }
    setErrors(error);
    if (Object.keys(error).filter((k) => error[k]).length) {
      return false;
    }

    const valid = [] as string[];
    const pkMap = new Map();

    (rowData || [])
      .filter((o) => o.crudKey !== CrudCode.DELETE)
      .forEach((o, index) => {
        if (_.isEmpty(o.mdlId)) {
          valid.push(
            `${index + 1} : ${t('md.label.모델을 선택해 주세요.', '모델을 선택해 주세요.')}`
          );
          return;
        }
        if (_.isEmpty(o.prdnProcCd)) {
          valid.push(
            `${index + 1} : ${t('md.label.공정을 선택해 주세요.', '공정을 선택해 주세요.')}`
          );
          return;
        }
        if (_.isEmpty(o.eqclId)) {
          valid.push(
            `${index + 1} : ${t('md.label.설비군을 선택해 주세요.', '설비군을 선택해 주세요.')}`
          );
          return;
        }
        if (_.isEmpty(o.stndYn)) {
          valid.push(
            `${index + 1} : ${t('md.label.구분을 선택해 주세요.', '구분을 선택해 주세요.')}`
          );
          return;
        }
        if (_.isEmpty(o.ivesAtcId)) {
          valid.push(
            `${index + 1} : ${t('md.label.투자항목을 선택해 주세요.', '투자항목을 선택해 주세요.')}`
          );
          return;
        }
        if (_.isEmpty(o.ivesDtlDesc)) {
          valid.push(
            `${index + 1} : ${t('md.label.투자상세를 입력해 주세요.', '투자상세를 입력해 주세요.')}`
          );
          return;
        }
        if (_.isEmpty(o.quotUprc)) {
          valid.push(
            `${index + 1} : ${t('md.label.단가를 입력해 주세요.', '단가를 입력해 주세요.')}`
          );
          return;
        }
        // TODO 확인필요 모델, 공정, 설비, 투자항목, 투자상세
        const pkKey = `${o.mdlId}|${o.prdnProcCd}|${o.eqclId}|${o.ivesAtcId}|${o.ivesDtlDesc}`;
        if (pkMap.has(pkKey)) {
          valid.push(
            `${index + 1} : ${t(
              'md.label.중복된 데이터가 존재합니다.',
              '중복된 데이터가 존재합니다.'
            )}`
          );
          return;
        } else {
          pkMap.set(pkKey, index);
        }
      });

    if (valid.length) {
      openMessageBar({ type: 'error', content: valid.join('<br/>') });
      return false;
    }
    return true;
  };

  /**
   * 저장
   */
  const handleSave = () => {
    if (!validate()) return;
    const confirmMsg = masterData?.wbsCd
      ? t('com.label.저장하시겠습니까?', '저장하시겠습니까?')
      : t(
          'md.label.WBS가 없습니다.<br/>저장하시겠습니까?',
          'WBS가 없습니다.<br/>저장하시겠습니까?'
        );
    openCommonModal({
      modalType: 'confirm',
      content: confirmMsg,
      yesCallback: () => {
        saveMdResults(masterData, rowData).then((res) => {
          openMessageBar({
            type: res?.successOrNot === 'Y' ? 'confirm' : 'error',
            content:
              res?.successOrNot === 'Y'
                ? t('com.label.저장되었습니다.', '저장되었습니다.')
                : res.data && typeof res.data === 'string'
                ? res.data
                : t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
          });
          if (res?.successOrNot === 'Y') {
            if (locationState?.mode === PageMode.WRITE) {
              menuContext.closeCurrentTab(menuContext);
              setTimeout(() => {
                navigate(`/md/invest-history/detail?mdRltId=${res.data}`, {
                  state: {
                    mode: PageMode.EDIT,
                    upprMnuUrl: '/md/invest-history',
                    mnuId: `/md/invest-history/${PageMode.EDIT}`,
                    mnuNm: t('md.label.투자이력 Database 수정', '투자이력 Database 수정'),
                    mdRltId: res.data,
                  },
                });
              }, 100);
            } else {
              setLocationState((prev) => ({
                ...prev,
                mode: PageMode.EDIT,
                mdRltId: res.data,
              }));
              setMdRltId(res.data);
              handleSearch(res.data);
            }
          }
        });
      },
    });
  };

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('asst.label.투자정보', '투자정보')}</h3>
        </SubTitleGroup>
        {hasAuth && (
          <GlobalBtnGroup>
            {PageMode.READ === pageMode ? (
              <Button
                css={IconButton.button}
                className="edit"
                onClick={() => {
                  setPageMode(PageMode.EDIT);
                  gridRef.current?.collectionView.refresh();
                }}
                disableRipple
              >
                {t('com.button.수정', '수정')}
              </Button>
            ) : (
              <>
                <Button
                  css={IconButton.button}
                  className="list"
                  disableRipple
                  onClick={() => {
                    menuContext.closeCurrentTab(menuContext);
                    setTimeout(() => {
                      navigate(`/md/invest-history`);
                    }, 100);
                  }}
                >
                  {t('com.button.목록', '목록')}
                </Button>
                <Button
                  css={IconButton.button}
                  className="icoNone"
                  disableRipple
                  onClick={handleReset}
                >
                  {t('com.button.초기화', '초기화')}
                </Button>
                <Button css={IconButton.button} className="save" onClick={handleSave}>
                  {t('com.label.저장', '저장')}
                </Button>
              </>
            )}
          </GlobalBtnGroup>
        )}
      </SubTitleLayout>

      <TableContainer css={tb.table} className="colFix9">
        <Table>
          <TableBody>
            <TableRow>
              <TableCell>
                <span>{String(t('md.label.WBS', 'WBS'))}</span>
              </TableCell>
              <TableCell colSpan={3}>
                {isEditable('wbsCd') ? (
                  <CustomInputWithSearch
                    name="wbsCd"
                    value={masterData?.wbsCd || ''}
                    placeholder={String(t('md.msg.WBS를 입력해 주세요.', 'WBS를 입력해 주세요.'))}
                    onChange={(e) => handleMasterChange('wbsCd', e.target.value)}
                    isError={errors?.wbsCd}
                    msgError={String(t('md.msg.WBS를 입력해 주세요.', 'WBS를 입력해 주세요.'))}
                  />
                ) : (
                  masterData?.wbsCd
                )}
              </TableCell>
              <TableCell>
                <span className={isEditable('bildPlntCd') ? 'dot' : ''}>
                  {String(t('md.label.플랜트', '플랜트'))}
                </span>
              </TableCell>
              <TableCell>
                {isEditable('bildPlntCd') ? (
                  <ComboBox
                    options={code?.bildPlntCd || []}
                    placeholder={String(t('com.label.선택', '선택'))}
                    defaultValue={masterData?.bildPlntCd || ''}
                    onChange={(value) => handleMasterChange('bildPlntCd', value)}
                    isError={errors?.bildPlntCd}
                    msgError={String(
                      t('md.label.플랜트를 선택해 주세요.', '플랜트를 선택해 주세요.')
                    )}
                  />
                ) : (
                  masterData?.bildPlntCdNm
                )}
              </TableCell>
              <TableCell>
                <span className={isEditable('cnsnAprvDt') ? 'dot' : ''}>
                  {String(t('md.label.품의일자', '품의일자'))}
                </span>
              </TableCell>
              <TableCell>
                {isEditable('cnsnAprvDt') ? (
                  <CustomDatepicker
                    isRange={false}
                    value={masterData?.cnsnAprvDt}
                    onChange={(newValue) => {
                      handleMasterChange('cnsnAprvDt', (newValue as string) || '');
                    }}
                    isError={errors?.cnsnAprvDt}
                    msgError={String(
                      t('md.label.품의일자를 선택해 주세요.', '품의일자를 선택해 주세요.')
                    )}
                  />
                ) : (
                  masterData?.cnsnAprvDt
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <span className={isEditable('wbsCdNm') ? 'dot' : ''}>
                  {String(t('md.label.투자명', '투자명'))}
                </span>
              </TableCell>
              <TableCell colSpan={7}>
                {isEditable('wbsCdNm') ? (
                  <CustomInputWithSearch
                    name="wbsCdNm"
                    value={masterData?.wbsCdNm || ''}
                    placeholder={String(
                      t('md.label.투자명을 입력해 주세요.', '투자명을 입력해 주세요.')
                    )}
                    onChange={(e) => handleMasterChange('wbsCdNm', e.target.value)}
                    isError={errors?.wbsCdNm}
                    msgError={String(
                      t('md.label.투자명을 입력해 주세요.', '투자명을 입력해 주세요.')
                    )}
                  />
                ) : (
                  masterData?.wbsCdNm
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <SubTitleLayout className="mt-6">
        <SubTitleGroup>
          <h3>{t('md.label.투자비 Database', '투자비 Database')}</h3>
          <span className="total">
            {t('com.label.총', '총')} <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          {hasAuth && (PageMode.EDIT === pageMode || PageMode.WRITE === pageMode) && (
            <>
              <Button
                css={IconButton.button}
                className="addRow"
                disableRipple
                onClick={handleAddRow}
              >
                {t('com.button.행추가', '행추가')}
              </Button>
              <Button
                css={IconButton.button}
                className="copyRow"
                disableRipple
                onClick={handleCopyRow}
              >
                {t('com.button.행복사', '행복사')}
              </Button>
              <Button
                css={IconButton.button}
                className="delRow"
                disableRipple
                onClick={handleDeleteRow}
              >
                {t('com.button.행삭제', '행삭제')}
              </Button>
            </>
          )}
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleDownloadExcel}
            disableRipple
          >
            {t('com.button.다운로드', '화면 다운로드')}
          </Button>
          <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => handleSearch(mdRltId)}
            disableRipple
          >
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={600}
        frozenColumns={3}
        isReadOnly={false}
        isSelector={true}
        allowPinning={false}
        initialized={onInitialized}
        beginningEdit={onBeginningEdit}
        cellEditEnding={onCellEditEnding}
        cellEditEnded={onCellEditEnded}
      />

      {hasAuth && (
        <GlobalBtnGroup>
          {PageMode.READ === pageMode ? (
            <Button
              css={IconButton.button}
              className="edit"
              onClick={() => {
                setPageMode(PageMode.EDIT);
                gridRef.current?.collectionView.refresh();
              }}
              disableRipple
            >
              {t('com.button.수정', '수정')}
            </Button>
          ) : (
            <>
              <Button
                css={IconButton.button}
                className="list"
                disableRipple
                onClick={() => {
                  menuContext.closeCurrentTab(menuContext);
                  setTimeout(() => {
                    navigate(`/md/invest-history`);
                  }, 100);
                }}
              >
                {t('com.button.목록', '목록')}
              </Button>
              <Button
                css={IconButton.button}
                className="icoNone"
                disableRipple
                onClick={handleReset}
              >
                {t('com.button.초기화', '초기화')}
              </Button>
              <Button css={IconButton.button} className="save" onClick={handleSave} disableRipple>
                {t('com.button.저장', '저장')}
              </Button>
            </>
          )}
        </GlobalBtnGroup>
      )}
    </>
  );
};

export default MdIvesHisInqRegPage;
